import { api } from "../../api/client";
import { useApi } from "../../hooks/useApi";
import { Icon } from "../../shell/Icon";

export function AngebotTab({
  tenantId,
  anfrageId,
}: {
  tenantId: string;
  anfrageId: string;
}) {
  const { state, refetch } = useApi(
    () => api.getAngebotManifest(tenantId, anfrageId),
    [tenantId, anfrageId]
  );

  if (state.status === "loading") {
    return (
      <div className="ueber">
        <section className="block">
          <div className="block-head">
            <span className="uppercase-mini">// ANGEBOT LADEN …</span>
          </div>
        </section>
      </div>
    );
  }

  if (state.status === "error") {
    if (state.error.message.includes("404")) {
      return (
        <div className="ueber">
          <section className="block" style={{ borderColor: "oklch(0.82 0.15 75 / 0.4)" }}>
            <div className="block-head">
              <span className="uppercase-mini" style={{ color: "var(--warn)" }}>
                // ANGEBOT NOCH NICHT ERZEUGT
              </span>
            </div>
            <div className="notice-body">
              <div>Pipeline lief noch nicht durch Phase 6 (Angebots-Generator).</div>
            </div>
          </section>
        </div>
      );
    }
    return (
      <div className="ueber">
        <section className="block" style={{ borderColor: "oklch(0.72 0.18 25 / 0.4)" }}>
          <div className="block-head">
            <span className="uppercase-mini" style={{ color: "var(--bad)" }}>
              // ANGEBOT NICHT LADBAR
            </span>
          </div>
          <div className="notice-body">
            <div className="mono" style={{ color: "var(--bad)" }}>
              {state.error.message}
            </div>
            <div>
              <button type="button" className="btn" onClick={refetch}>
                <Icon name="refresh" size={14} /> Nochmal versuchen
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const angebot = state.data;
  const veraltet = angebot.veraltet === true;
  const staleSeitLabel = angebot.staleSeit
    ? new Date(angebot.staleSeit).toLocaleString("de-DE")
    : null;

  return (
    <div className="ueber">
      {veraltet && (
        <section
          className="block"
          style={{ borderColor: "oklch(0.82 0.15 75 / 0.4)" }}
        >
          <div className="block-head">
            <span className="uppercase-mini" style={{ color: "var(--warn)" }}>
              // ANGEBOT VERALTET{staleSeitLabel ? ` · seit ${staleSeitLabel}` : ""}
            </span>
          </div>
          <div className="notice-body">
            <div>
              Phase 4 oder 5 wurden seit der letzten Generierung geaendert.
              Summe + Positionsanzahl sind aktuell, aber die Dateien
              (xlsx, pdf) zeigen noch den alten Stand. Pipeline ab Phase 6
              neu rennen, bevor das Angebot rausgeht.
            </div>
          </div>
        </section>
      )}

      <div className="metric-grid">
        <MetricSmall label="POSITIONEN" value={String(angebot.positionenAnzahl)} />
        <MetricSmall
          label="SUMME NETTO"
          // Angebots-Manifest hat keine Waehrung-Spalte — bidi-handel ist
          // single-currency (EUR). Bei Multi-Currency müssten wir hier den
          // Tenant-Default aus stammdaten-summary nachladen.
          value={`${fmtEur(angebot.summeNetto, "EUR")}`}
          accent
        />
        <MetricSmall
          label="ARTEFAKTE"
          value={`${angebot.artefakte.length} Datei${angebot.artefakte.length === 1 ? "" : "en"}`}
        />
      </div>

      <section className="block ready">
        <div className="block-head">
          <span
            className="uppercase-mini"
            style={{ color: veraltet ? "var(--warn)" : "var(--ok)" }}
          >
            {veraltet
              ? "// ANGEBOT · VERALTET (Dateien zum Download weiter verfuegbar)"
              : "// ANGEBOT · BEREIT ZUM VERSAND"}
          </span>
        </div>
        <div className="angebot-grid">
          {angebot.artefakte.map((a) => (
            <div key={a.format} className="angebot-file">
              <div className="angebot-ico">
                <Icon name="download" size={20} />
              </div>
              <div>
                <div className="angebot-name mono">
                  {a.relPath.split("/").pop() ?? `angebot.${a.format}`}
                </div>
                <div className="angebot-meta uppercase-mini">
                  {fmtBytes(a.groesseBytes)} · {a.format.toUpperCase()}
                </div>
              </div>
              <a
                href={api.angebotDownloadUrl(
                  tenantId,
                  anfrageId,
                  a.format as "xlsx" | "pdf"
                )}
                className={`btn sm ${a.format === "xlsx" ? "primary" : ""}`}
              >
                Download
              </a>
            </div>
          ))}
        </div>
      </section>

      <section className="block">
        <div className="block-head">
          <span className="uppercase-mini">// ANSCHREIBEN</span>
        </div>
        <div
          style={{
            whiteSpace: "pre-line",
            padding: "16px 18px",
            background: "var(--bg-raised)",
            border: "1px solid var(--line-soft)",
            borderRadius: "var(--r)",
            fontSize: 13,
            lineHeight: 1.6,
            color: "var(--fg-mute)",
          }}
        >
          {angebot.anschreibenText || (
            <em className="dim">Kein Anschreibentext gespeichert.</em>
          )}
        </div>
      </section>

      <section className="block">
        <div className="block-head">
          <span className="uppercase-mini">// ARTEFAKT-MANIFEST</span>
        </div>
        <div style={{ display: "grid", gap: 4 }}>
          <div
            className="log-row"
            style={{
              gridTemplateColumns: "70px 1fr 100px 140px",
              color: "var(--fg-dim)",
            }}
          >
            <span className="uppercase-mini">Format</span>
            <span className="uppercase-mini">Pfad</span>
            <span className="uppercase-mini" style={{ textAlign: "right" }}>
              Bytes
            </span>
            <span className="uppercase-mini">Erzeugt</span>
          </div>
          {angebot.artefakte.map((a) => (
            <div
              key={a.relPath}
              className="log-row"
              style={{ gridTemplateColumns: "70px 1fr 100px 140px" }}
            >
              <span className="mono" style={{ textTransform: "uppercase", fontSize: 11 }}>
                {a.format}
              </span>
              <span
                className="mono"
                style={{ fontSize: 11, color: "var(--fg-dim)", wordBreak: "break-all" }}
              >
                {a.relPath}
              </span>
              <span className="mono" style={{ fontSize: 11, textAlign: "right" }}>
                {a.groesseBytes.toLocaleString("de-DE")}
              </span>
              <span className="mono" style={{ fontSize: 11, color: "var(--fg-dim)" }}>
                {new Date(a.erzeugtAm).toLocaleString("de-DE")}
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function MetricSmall({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className={`metric ${accent ? "acc" : ""}`}>
      <div className="metric-label uppercase-mini">{label}</div>
      <div className="metric-value mono" style={{ fontSize: 24 }}>
        {value}
      </div>
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtEur(n: number, waehrung: string): string {
  return `${fmt(n)} ${waehrung}`;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
